import * as React from 'react'
import { Check, Minus } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface CheckboxProps {
  checked: boolean
  /** Tri-state: when true, renders a dash instead of a tick (some-but-not-all). */
  indeterminate?: boolean
  onCheckedChange: (checked: boolean) => void
  disabled?: boolean
  /** Accessible label — required since the control renders no visible text. */
  'aria-label': string
  className?: string
}

/**
 * Minimal dark-mode checkbox. Built on a native button (role=checkbox) rather
 * than pulling in @radix-ui/react-checkbox — the locked stack already ships the
 * other Radix primitives, but a new dependency isn't warranted for a tick box.
 */
export const Checkbox = React.forwardRef<HTMLButtonElement, CheckboxProps>(
  ({ checked, indeterminate, onCheckedChange, disabled, className, ...props }, ref) => {
    const isChecked = checked || indeterminate
    return (
      <button
        ref={ref}
        type="button"
        role="checkbox"
        aria-checked={indeterminate ? 'mixed' : checked}
        aria-label={props['aria-label']}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation()
          onCheckedChange(!checked)
        }}
        className={cn(
          'inline-flex h-4 w-4 shrink-0 items-center justify-center border transition-colors',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary',
          'disabled:pointer-events-none disabled:opacity-40',
          isChecked
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-cmd-outline bg-transparent text-transparent hover:border-white',
          className,
        )}
      >
        {indeterminate ? (
          <Minus className="h-3 w-3" aria-hidden="true" />
        ) : checked ? (
          <Check className="h-3 w-3" aria-hidden="true" />
        ) : null}
      </button>
    )
  },
)
Checkbox.displayName = 'Checkbox'
